import { Locale } from "@rr/utils";

export type FetcherConfig = {
  baseUrl: string;
  token?: string;
  /**
   * Reads the token per request, so a client built before a refresh still
   * sends the current one. Takes precedence over `token`.
   */
  getToken?: () => string | null | undefined;
  /**
   * Called on a 401 with the token the request was sent with. Returns the
   * token to retry with (once), or null to throw the 401. The app owns the
   * refresh and sign-out policy; this package only asks.
   */
  onUnauthorized?: (sentToken: string | undefined, response: Response) => Promise<string | null>;
  locale?: Locale;
};

export type FetchOptions = RequestInit & {
  params?: Record<string, any>;
};

export class ApiError extends Error {
  constructor(
    public status: number,
    public message: string,
    public data?: any,
    /** Backend error code, e.g. "INVOICE.NO_SCENARIO", when one was sent. */
    public code?: string
  ) {
    super(message);
    this.name = "ApiError";
  }
}

export function createFetcher({ baseUrl, token, getToken, onUnauthorized, locale }: FetcherConfig) {
  return async <T>(path: string, options: FetchOptions = {}): Promise<T> => {
    const { params, ...init } = options;
    
    // Build URL with query params
    let url = `${baseUrl}${path}`;
    if (params) {
      const searchParams = new URLSearchParams();
      Object.entries(params).forEach(([key, value]) => {
        if (value !== undefined && value !== null) {
          searchParams.append(key, String(value));
        }
      });
      const query = searchParams.toString();
      if (query) url += `?${query}`;
    }

    // Don't force a JSON content-type for FormData uploads — the browser
    // needs to set its own multipart/form-data boundary.
    const isFormData = typeof FormData !== "undefined" && init.body instanceof FormData;

    const headers: HeadersInit = {
      ...(isFormData ? {} : { "Content-Type": "application/json" }),
      "X-Language": locale || "ru",
      ...(init.headers || {}),
    };

    const sentToken = (getToken ? getToken() : token) ?? undefined;
    if (sentToken) {
      (headers as any)["Authorization"] = `Bearer ${sentToken}`;
    }

    let response = await fetch(url, {
      ...init,
      headers,
    });

    if (response.status === 401 && onUnauthorized) {
      const retryToken = await onUnauthorized(sentToken, response);
      if (retryToken) {
        response = await fetch(url, {
          ...init,
          headers: { ...headers, Authorization: `Bearer ${retryToken}` },
        });
      }
    }

    if (!response.ok) {
      let errorData;
      try {
        errorData = await response.json();
      } catch {
        errorData = { message: response.statusText };
      }
      // The API wraps failures as `{ success: false, error: { code, message } }`
      // — the message is nested, so reading `errorData.message` turned every
      // backend error into "Request failed" for callers that surface
      // `err.message`. Prefer the envelope, then a bare `message` (the
      // statusText fallback above and any non-conforming endpoint), then give up.
      const envelope = errorData?.error;
      throw new ApiError(
        response.status,
        envelope?.message || errorData?.message || "Request failed",
        errorData,
        envelope?.code,
      );
    }

    // Handle empty responses
    if (response.status === 204) return {} as T;

    return response.json() as Promise<T>;
  };
}

export type Fetcher = ReturnType<typeof createFetcher>;
